/**
 * Global DOM event handlers.
 *
 * Replaces the event handlers from GlobalEvents.ts. Self-attaching on import.
 */

import { App } from '@/services/app-compat';
import { flushSessionCheckTelemetry } from '@/services/session-check-telemetry';
import { installUiEventTrail, getUiEventTrailReport, isUpdateDepthError } from '@/services/ui-event-trail';
// NOTE: api is accessed lazily via (window as any).api because api-class.ts
// initializes after global-events.ts in the module graph.

// ── Helper ────────────────────────────────────────────────────────

function getAppVersionFromApp() {
  if ((window as any).isIOSApp) {
    (window as any).notifyApp("getVersion", []);
  } else if ((window as any).isAndroidApp) {
    (window as any).notifyApp("getVersion", []);
    setTimeout(() => {
      (window as any).notifyAndroidVersionUpdate();
    }, 1000);
  } else if ((window as any).isWindowsAppSdkApp) {
    (window as any).notifyApp("getVersion", []);
    setTimeout(() => {
      (window as any).notifyWindowsAppSdkAppVersionUpdate();
    }, 750);
  }
}

// ── Login form click handler ──────────────────────────────────────

document.addEventListener("click", function (e) {
  const target = (e.target as HTMLElement).closest('#btnSignIn') as HTMLButtonElement;
  if (!target) return;
  const btnSignIn = target;
  const usernameEl = document.getElementById("inpUsername") as HTMLInputElement;
  const passwordEl = document.getElementById("inpPassword") as HTMLInputElement;
  const lblAccountInfo = document.getElementById("lblAccountInfo") as HTMLElement;
  btnSignIn.disabled = true;
  const usernameVal: string = usernameEl.value;
  const passwordVal: string = passwordEl.value;

  if (!usernameVal || !passwordVal) {
    btnSignIn.disabled = false;
    return;
  }

  if (usernameVal.includes("@")) {
    App.showPopupNotification("Please log in to client portal at https://securityclientportal.com", "warning");
    btnSignIn.disabled = false;
    return;
  }

  const showAccountError = function (msg: string) {
    lblAccountInfo.style.display = 'block';
    lblAccountInfo.textContent = msg;
  };

  const fnLoginSuccess = function (result: any) {
    if ((window as any).hasValue(result.access_token)) {
      (window as any).setAuthObject(result);
    } else {
      (window as any).removeAuthObject();
    }
    (window as any).removeModuleTypeAndAvailableSubmodules();
    App.initPubNub();

    btnSignIn.disabled = false;
    usernameEl.value = "";
    passwordEl.value = "";
    lblAccountInfo.style.display = 'none';
    if ((window as any).isIOSApp) {
      (window as any).notifyApp("login", [(window as any).getCurrentAccessToken(), (window as any).api.iosDeviceTokenEndpoint]);
    } else if ((window as any).isAndroidApp) {
      (window as any).notifyApp("login", [(window as any).getCurrentAccessToken(), (window as any).api.androidDeviceTokenEndpoint]);
    } else if ((window as any).isWindowsAppSdkApp) {
      (window as any).notifyApp("login", [(window as any).getCurrentAccessToken()]);
    } else {
      (window as any).notifyApp("login", [(window as any).getCurrentAccessToken()]);
      if ((window as any).isUWPApp) {
        App.showPopupNotification("Please update this app to the latest version from Microsoft Store.", "warning");
      }
    }
    getAppVersionFromApp();
    flushSessionCheckTelemetry();

    App.preloadCompanyLogo();

    (window as any).OrnaReact.navigate.toHome();
  };

  const fnLoginErrorNewBackendVersion = function (_result: any) {
    App.latestBackendVersionCallback((latestVersion: number) => {
      const currentVersion: number = (window as any).api.getCurrentBackendVersion();
      if (currentVersion > 0 && latestVersion > 0 && currentVersion !== latestVersion) {
        const fnLoginSuccessInner = function (resultInner: any) {
          fnLoginSuccess(resultInner);
          setTimeout(() => {
            (window as any).reloadPageSkipBrowserCache();
          }, 0);
        };
        const fnErrorResultInner = function (errorResultInner: any) {
          (window as any).api.setCurrentBackendVersion(currentVersion);
          if (errorResultInner.status === 403) {
            showAccountError(`There was an issue connecting to Orna. Please close and reopen your ${(window as any).getAppOrBrowserString()}, and try again.`);
            btnSignIn.disabled = false;
          } else {
            showAccountError("Username or password is incorrect. Please try again.");
            btnSignIn.disabled = false;
          }
        };

        (window as any).api.setCurrentBackendVersion(latestVersion);
        (window as any).api.login(usernameVal, passwordVal, fnLoginSuccessInner, fnErrorResultInner);
      } else {
        showAccountError(`There was an issue connecting to Orna. Please close and reopen your ${(window as any).getAppOrBrowserString()}, and try again.`);
        btnSignIn.disabled = false;
      }
    });
  };

  const fnLoginError = function (errorResult: any) {
    if (errorResult.status === 403) {
      fnLoginErrorNewBackendVersion(errorResult);
    } else {
      const kstAuthenticateError = errorResult.getResponseHeader((window as any).KST_AUTHENTICATE_HEADER);
      const UnauthorizedErrors = (window as any).UnauthorizedErrors;
      if (kstAuthenticateError === UnauthorizedErrors.USER_ACCOUNT_EXPIRED) {
        showAccountError("Account disabled. Please contact an administrator at your organization.");
      } else if (kstAuthenticateError === UnauthorizedErrors.USER_ACCOUNT_DISABLED) {
        showAccountError("Account locked. Please contact an administrator at your organization.");
      } else if (kstAuthenticateError === UnauthorizedErrors.ORG_DISABLED) {
        showAccountError("Organization disabled. Please contact an administrator at your organization.");
      } else {
        showAccountError("Username or password is incorrect. Please try again.");
      }
      btnSignIn.disabled = false;
    }
  };

  (window as any).api.login(usernameVal, passwordVal, fnLoginSuccess, fnLoginError);
});

// ── Call card link click handlers ─────────────────────────────────

function getExistingCadCallCardRelativeUrl(callCardId: any) {
  return (window as any).OrnaReact.navigate.getExistingCadCallCardUrl(callCardId);
}

document.addEventListener("click", function (e) {
  const target = (e.target as HTMLElement).closest('#lnkEvent,#lnkMapEvent,#lnkEventMDT,#lnkMapEventMDT') as HTMLElement;
  if (!target) return;
  const callCardId = target.title;
  if (target.id === "lnkEvent") {
    (window as any).OrnaReact.navigate.toCallCard(callCardId, 'CAD');
  } else if (target.id === "lnkEventMDT") {
    (window as any).OrnaReact.navigate.toCallCard(callCardId, 'MDT');
  } else if (target.id === "lnkMapEventMDT") {
    (window as any).OrnaReact.navigate.toMapByCallCardId(callCardId, 'MDT');
  } else {
    (window as any).OrnaReact.navigate.toMapByCallCardId(callCardId, 'CAD');
  }
  e.preventDefault();
});

// ── Case report link click handler ────────────────────────────────

document.addEventListener("click", function (e) {
  const target = (e.target as HTMLElement).closest('#lnkCaseId') as HTMLElement;
  if (!target) return;
  const caseReportId: string = (target as HTMLElement).dataset.caseReportId!;
  const moduleType: string = App.getModuleTypeFromURL();
  (window as any).OrnaReact.navigate.toCaseReport(caseReportId, false, false, moduleType.toUpperCase());
  e.preventDefault();
});

// ── Recover username click handler ────────────────────────────────

document.addEventListener("click", function (e) {
  const target = (e.target as HTMLElement).closest('#lnkRecoverUsername') as HTMLElement;
  if (!target) return;
  (window as any).kstPrompt({
    title: 'Recover Username',
    message: 'Enter in your primary email associated with your profile below, and you will receive an email with your username(s).',
    placeholder: 'Enter your email address here...',
    submitText: 'Recover Username',
    inputType: 'email',
    theme: ''
  }).then(function (email: string) {
    if (email) {
      (window as any).api.recoverUsername({ email: email }, (_result: any) => {
        App.showPopupNotification("Email Sent", "success");
      });
    }
  });
});

// ── Forgot password click handler ─────────────────────────────────

document.addEventListener("click", function (e) {
  const target = (e.target as HTMLElement).closest('#lnkForgotPassword') as HTMLElement;
  if (!target) return;
  (window as any).kstPrompt({
    title: 'Forgot Password?',
    message: 'Enter in your username below, and you will receive an email to reset your password.',
    placeholder: 'Enter your username here...',
    submitText: 'Reset Password',
    theme: ''
  }).then(function (username: string) {
    if (username) {
      (window as any).api.passwordResetByUsername({ username: username }, (_result: any) => {
        App.showPopupNotification("Email Sent", "success");
      });
    }
  });
});

// ── window.onerror handler ────────────────────────────────────────

installUiEventTrail();

window.onerror = (msg, url, lineNo, columnNo, error) => {
  const load = (window as any).load;
  if (load !== undefined && load !== null) {
    load.hideLoadingScreen(true);
  }

  var stack = "";
  if (error && error.stack !== undefined) stack = error.stack;

  var message = [
    `Message: ${msg}\n`,
    `URL: ${url}\n`,
    `Line: ${lineNo}\n`,
    `Column: ${columnNo}\n`,
    `Stack: ${stack}\n`
  ].join(" - ");

  // React #185 from an event handler has no component stack; the event trail shows
  // which field and which event sequence drove the update loop.
  if (isUpdateDepthError(msg)) {
    message += ` - UI trail: ${getUiEventTrailReport()}\n`;
  }

  const api = (window as any).api;
  if (api) {
    api.insertNewJSError(message, url, msg, (window as any).JSErrorType.UNCAUGHT, null);
  }
};

// ── unhandled promise rejection handler ───────────────────────────

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = reason instanceof Error
    ? `Unhandled Promise Rejection: ${reason.message}\nStack: ${reason.stack || ''}`
    : `Unhandled Promise Rejection: ${String(reason)}`;

  const api = (window as any).api;
  if (api) {
    api.insertNewJSError(message, window.location.href, null, (window as any).JSErrorType.UNCAUGHT, null);
  }
});

// ── Resize and orientation ────────────────────────────────────────

window.addEventListener("resize", function () {
  App.onWindowResize();
});

// ── Register on window ────────────────────────────────────────────

(window as any).getAppVersionFromApp = getAppVersionFromApp;
(window as any).getExistingCadCallCardRelativeUrl = getExistingCadCallCardRelativeUrl;
